import { motion } from "framer-motion";
import {
    CheckCircle2,
    Globe,
    Loader2,
    Pencil,
    Plus,
    Save,
    Target,
    Trash2,
    Users as UsersIcon,
    X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import ConfirmModal from "../components/common/ConfirmModal";
import AvatarUploader from "../components/common/AvatarUploader";
import { NAV_ITEMS } from "../config/navItems";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ROLES } from "../lib/constants";
import { initials } from "../lib/format";
import { effectiveRoles } from "../lib/roles";

const COMBINABLE_ROLES = [
    { key: "pre_recruteur", label: "Pré-recruteur", color: "bg-shariti-orange text-white", desc: "Appels & qualification téléphonique" },
    { key: "referent", label: "Référent terrain", color: "bg-emerald-600 text-white", desc: "Immersions & validation terrain" },
    { key: "coordinateur", label: "Coordinateur RH", color: "bg-indigo-600 text-white", desc: "Zone régionale + permissions cochables (badge, envoi fiche, validation, contrat)" },
];

// iter110l · Régions disponibles pour un Coordinateur RH — labels EXACTS
// tels que stockés dans `candidate.layan_offer_region`. Toute modification
// ici doit être miroir de `_BUILTIN_REGION_LABELS` côté backend.
const COORDINATEUR_REGIONS = [
    "Île-de-France",
    "Grand Est",
    "Alsace",
    "Nouvelle-Aquitaine",
    "Occitanie",
    "Provence-Alpes-Côte d'Azur",
    "Centre-Val de Loire",
];

const COORDINATEUR_PERMISSIONS = [
    { key: "can_download_badges", label: "Télécharger les badges", desc: "Badge individuel + export ZIP en masse" },
    { key: "can_send_onboarding", label: "Envoyer les fiches d'embauche", desc: "Déclencher l'envoi du dossier au candidat" },
    { key: "can_review_onboarding", label: "Valider / rejeter les dossiers", desc: "Approuver, demander correction ou rejeter un candidat" },
    { key: "can_view_contracts", label: "Suivre les contrats", desc: "Lecture des contrats en préparation, envoyés, signés" },
    { key: "can_manage_contracts", label: "Gérer les contrats de A à Z", desc: "Générer, modifier, envoyer pour signature, renouveler, annuler — pilotage autonome (inclut la lecture)" },
    { key: "can_remove_from_pipeline", label: "Retirer du circuit", desc: "Sortir un candidat du pipeline (set_aside) pour désengorger sa colonne À déclencher" },
];

export default function Users() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingScope, setEditingScope] = useState(null);   // user being edited (scope only)
    const [editingUser, setEditingUser] = useState(null);     // user being edited (full)
    const [creating, setCreating] = useState(false);
    const [deleting, setDeleting] = useState(null);           // user being confirmed for deletion

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: u }, { data: o }] = await Promise.all([
                api.get("/users"),
                api.get("/integrations/layan/offers"),
            ]);
            setUsers(u);
            setOffers(o);
        } catch {
            toast.error("Erreur de chargement");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function saveAssignment(userId, offerIds) {
        try {
            await api.patch(`/users/${userId}/assigned-offers`, {
                assigned_offer_ids: offerIds,
            });
            setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, assigned_offer_ids: offerIds } : u));
            toast.success(offerIds.length === 0 ? "Restriction levée — l'utilisateur voit toutes les offres" : `${offerIds.length} offre${offerIds.length > 1 ? "s" : ""} attribuée${offerIds.length > 1 ? "s" : ""}`);
            setEditingScope(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    async function toggleEmbaucheAccess(userId, next) {
        // iter84 · Toggle inline "Accès Embauche & Contrats".
        try {
            await api.patch(`/users/${userId}`, { can_access_embauche: next });
            setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, can_access_embauche: next } : u));
            toast.success(next
                ? "Accès Embauche & Contrats activé"
                : "Accès Embauche & Contrats retiré");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    async function handleCreate(payload) {
        try {
            const { data } = await api.post("/users", payload);
            setUsers((prev) => [
                ...prev,
                {
                    ...data,
                    additional_roles: payload.additional_roles || [],
                    assigned_offer_ids: payload.assigned_offer_ids || [],
                },
            ]);
            toast.success(`Utilisateur créé : ${data.name}`);
            setCreating(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    async function handleUpdate(userId, payload) {
        try {
            const { data } = await api.patch(`/users/${userId}`, payload);
            setUsers((prev) => prev.map((u) => u.id === userId ? data : u));
            toast.success("Utilisateur mis à jour");
            setEditingUser(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    async function handleDelete(userId) {
        try {
            await api.delete(`/users/${userId}`);
            setUsers((prev) => prev.filter((u) => u.id !== userId));
            toast.success("Utilisateur supprimé");
            setDeleting(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-shariti-orange" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="users-page">
            <header className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                        <UsersIcon className="h-3 w-3" /> Back-office
                    </div>
                    <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                        Équipe Shariti
                    </h1>
                    <p className="mt-2 text-sm font-medium text-slate-500 max-w-2xl">
                        {users.length} membre{users.length > 1 ? "s" : ""} actif{users.length > 1 ? "s" : ""}. Créez, modifiez et attribuez à chaque pré-recruteur ou référent les offres Layan sur lesquelles il doit se concentrer.
                    </p>
                </div>
                <button
                    onClick={() => setCreating(true)}
                    data-testid="users-create-btn"
                    className="btn-shariti"
                >
                    <Plus className="h-4 w-4" />
                    Nouvel utilisateur
                </button>
            </header>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {users.map((u) => {
                    const userRolesSet = effectiveRoles(u);
                    const userRoles = Array.from(userRolesSet);
                    const assigned = u.assigned_offer_ids || [];
                    const isAssignable = userRolesSet.has("pre_recruteur") || userRolesSet.has("referent");
                    const isMe = u.id === me?.id;
                    return (
                        <motion.div
                            key={u.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="card-soft p-5 space-y-3 relative"
                            data-testid={`user-${u.id}`}
                        >
                            <div className="flex items-center gap-4">
                                {u.avatar_url ? (
                                    <img src={u.avatar_url} alt="" className="h-12 w-12 rounded-2xl object-cover" />
                                ) : (
                                    <div className="h-12 w-12 rounded-2xl bg-shariti-orange-soft text-shariti-orange font-extrabold flex items-center justify-center">
                                        {initials(u.name)}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <div className="text-base font-extrabold text-shariti-black truncate">{u.name}</div>
                                        {isMe && <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-shariti-cream text-slate-500 border border-shariti-gray">moi</span>}
                                    </div>
                                    <div className="text-xs font-medium text-slate-500 truncate">{u.email}</div>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {userRoles.map((r) => (
                                            <span key={r} className="text-[9px] font-bold uppercase tracking-widest text-shariti-orange bg-shariti-orange-soft px-1.5 py-0.5 rounded-full">
                                                {ROLES[r]?.short || r}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {isAssignable && (
                                <div className="rounded-2xl bg-shariti-cream/60 border border-shariti-gray p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        {assigned.length === 0 ? (
                                            <Globe className="h-3.5 w-3.5 text-emerald-600" />
                                        ) : (
                                            <Target className="h-3.5 w-3.5 text-shariti-orange" />
                                        )}
                                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                            Périmètre offres
                                        </div>
                                    </div>
                                    {assigned.length === 0 ? (
                                        <div className="text-xs font-bold text-emerald-700">Voit toutes les offres</div>
                                    ) : (
                                        <div className="space-y-1">
                                            <div className="text-xs font-bold text-shariti-orange">
                                                Restreint à {assigned.length} offre{assigned.length > 1 ? "s" : ""}
                                            </div>
                                            <ul className="text-[11px] font-medium text-slate-600 space-y-0.5 max-h-20 overflow-y-auto">
                                                {assigned.map((oid) => {
                                                    const o = offers.find((x) => x.layan_offer_id === oid);
                                                    return (
                                                        <li key={oid} className="truncate" title={o?.title || `#${oid}`}>
                                                            · {o?.title || `Offre #${oid}`}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    )}
                                    <button
                                        onClick={() => setEditingScope(u)}
                                        data-testid={`user-${u.id}-edit-scope`}
                                        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-orange hover:border-shariti-orange px-3 h-8 font-bold text-[11px] transition"
                                    >
                                        {assigned.length === 0 ? "Restreindre à des offres" : "Modifier le périmètre"}
                                    </button>
                                </div>
                            )}

                            {/* iter84 · Toggle inline "Accès Embauche & Contrats"
                                — visible uniquement pour les non-admins (l'admin y a droit d'office). */}
                            {!userRolesSet.has("admin") && (
                                <div
                                    className="rounded-2xl bg-shariti-cream/60 border border-shariti-gray p-3 flex items-start gap-3"
                                    data-testid={`user-${u.id}-embauche-access-block`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
                                            Accès Embauche & Contrats
                                        </div>
                                        <div className="text-[11px] font-medium text-slate-600 leading-snug">
                                            {u.can_access_embauche
                                                ? "Peut voir le kanban Embauche & Contrats et cocher « Onboardé PayFit »."
                                                : "Aucun accès. Active pour lui déléguer le suivi PayFit."}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => toggleEmbaucheAccess(u.id, !u.can_access_embauche)}
                                        data-testid={`user-${u.id}-toggle-embauche`}
                                        role="switch"
                                        aria-checked={!!u.can_access_embauche}
                                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${u.can_access_embauche ? "bg-shariti-orange" : "bg-slate-300"
                                            }`}
                                    >
                                        <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${u.can_access_embauche ? "translate-x-6" : "translate-x-1"
                                                }`}
                                        />
                                    </button>
                                </div>
                            )}

                            <div className="flex items-center gap-2 pt-1">
                                <button
                                    onClick={() => setEditingUser(u)}
                                    data-testid={`user-${u.id}-edit`}
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-orange hover:border-shariti-orange px-3 h-8 font-bold text-[11px] transition"
                                >
                                    <Pencil className="h-3 w-3" /> Modifier
                                </button>
                                {!isMe && (
                                    <button
                                        onClick={() => setDeleting(u)}
                                        data-testid={`user-${u.id}-delete`}
                                        className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-red-200 text-shariti-red hover:bg-red-50 px-3 h-8 font-bold text-[11px] transition"
                                        title="Supprimer cet utilisateur"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            {editingScope && (
                <ScopeEditor
                    user={editingScope}
                    offers={offers}
                    onCancel={() => setEditingScope(null)}
                    onSave={(ids) => saveAssignment(editingScope.id, ids)}
                />
            )}

            {creating && (
                <UserEditor
                    mode="create"
                    offers={offers}
                    onCancel={() => setCreating(false)}
                    onSubmit={handleCreate}
                />
            )}

            {editingUser && (
                <UserEditor
                    mode="edit"
                    user={editingUser}
                    offers={offers}
                    onCancel={() => setEditingUser(null)}
                    onSubmit={(payload) => handleUpdate(editingUser.id, payload)}
                />
            )}

            {deleting && (
                <ConfirmModal
                    title="Supprimer cet utilisateur ?"
                    body={<>L'utilisateur <strong>{deleting.name}</strong> ({deleting.email}) sera supprimé définitivement et ne pourra plus se connecter. Action irréversible.</>}
                    confirmLabel="Supprimer définitivement"
                    tone="red"
                    onCancel={() => setDeleting(null)}
                    onConfirm={() => handleDelete(deleting.id)}
                />
            )}
        </div>
    );
}

function UserEditor({ mode, user, offers, onCancel, onSubmit }) {
    const isCreate = mode === "create";
    const [name, setName] = useState(user?.name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [password, setPassword] = useState("");
    // Roles state: an admin user has roleSet = {admin}, otherwise any combo of pre/ref.
    const initialRoles = (() => {
        if (!user) return new Set(["pre_recruteur"]);
        const all = new Set([user.role]);
        for (const r of user.additional_roles || []) all.add(r);
        return all;
    })();
    const [roleSet, setRoleSet] = useState(initialRoles);
    const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || "");
    const [phone, setPhone] = useState(user?.phone || "");
    const [assigned, setAssigned] = useState(new Set(user?.assigned_offer_ids || []));
    const [search, setSearch] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // iter110l · Coordinateur RH — état régions + permissions
    const [regions, setRegions] = useState(new Set(user?.assigned_regions || []));
    const [perms, setPerms] = useState({
        can_download_badges: !!user?.can_download_badges,
        can_send_onboarding: !!user?.can_send_onboarding,
        can_review_onboarding: !!user?.can_review_onboarding,
        can_view_contracts: !!user?.can_view_contracts,
        can_manage_contracts: !!user?.can_manage_contracts,
        can_remove_from_pipeline: !!user?.can_remove_from_pipeline,
    });
    // CEO 2026-09-30 · Set des paths d'onglets MASQUÉS pour ce user
    // (source of truth backend = `hidden_nav`). L'UI raisonne à l'inverse
    // (« Onglets visibles ») pour rester cohérente avec le langage
    // parlé — cocher = afficher.
    const [hiddenNav, setHiddenNav] = useState(new Set(user?.hidden_nav || []));

    const isAdmin = roleSet.has("admin");
    const isCoordinateur = roleSet.has("coordinateur");
    const isAssignable = roleSet.has("pre_recruteur") || roleSet.has("referent");

    function toggleRegion(r) {
        const next = new Set(regions);
        next.has(r) ? next.delete(r) : next.add(r);
        setRegions(next);
    }
    function togglePerm(k) {
        setPerms((p) => ({ ...p, [k]: !p[k] }));
    }

    function setAdminExclusive() {
        setRoleSet(new Set(["admin"]));
    }
    function toggleRole(role) {
        const next = new Set(roleSet);
        // Admin is exclusive — selecting admin clears the others; selecting pre/ref clears admin.
        if (role === "admin") {
            setAdminExclusive();
            return;
        }
        next.delete("admin");
        if (next.has(role)) {
            next.delete(role);
        } else {
            next.add(role);
        }
        // Must keep at least one role.
        if (next.size === 0) next.add(role);
        setRoleSet(next);
    }

    function toggleOffer(oid) {
        const next = new Set(assigned);
        if (next.has(oid)) next.delete(oid);
        else next.add(oid);
        setAssigned(next);
    }

    // CEO 2026-09-30 · Liste des onglets ÉLIGIBLES au vu des rôles
    // actuellement sélectionnés dans la modale + `extraAccess` évalué
    // sur un user "prévisualisé" pour refléter en direct ce qui serait
    // visible. Recalculé à chaque changement de rôle ou permission.
    const previewUser = {
        role: roleSet.has("admin") ? "admin"
            : roleSet.has("pre_recruteur") ? "pre_recruteur"
            : roleSet.has("coordinateur") ? "coordinateur"
            : "referent",
        additional_roles: Array.from(roleSet).filter((r) => r !== "admin"),
        can_access_embauche: !!user?.can_access_embauche,
        can_send_onboarding: !!perms.can_send_onboarding,
        can_review_onboarding: !!perms.can_review_onboarding,
        can_view_contracts: !!perms.can_view_contracts,
        can_download_badges: !!perms.can_download_badges,
        can_manage_contracts: !!perms.can_manage_contracts,
        can_remove_from_pipeline: !!perms.can_remove_from_pipeline,
    };
    const roleSetPreview = new Set([previewUser.role, ...previewUser.additional_roles]);
    const eligibleTabs = NAV_ITEMS.filter((i) =>
        i.roles.some((r) => roleSetPreview.has(r))
        || (typeof i.extraAccess === "function" && i.extraAccess(previewUser))
    );
    function toggleTab(to) {
        const next = new Set(hiddenNav);
        // Case cochée = visible → path ABSENT de hiddenNav
        // Case décochée = masqué → path PRESENT dans hiddenNav
        if (next.has(to)) next.delete(to);
        else next.add(to);
        setHiddenNav(next);
    }

    const filteredOffers = offers.filter(
        (o) => !search
            || o.title.toLowerCase().includes(search.toLowerCase())
            || o.city?.toLowerCase().includes(search.toLowerCase())
            || String(o.layan_offer_id).includes(search),
    );

    async function submit(e) {
        e?.preventDefault();
        if (!name.trim()) { toast.error("Nom requis"); return; }
        if (roleSet.size === 0) { toast.error("Sélectionnez au moins un rôle"); return; }
        if (isCreate) {
            if (!email.trim()) { toast.error("Email requis"); return; }
            if (password.length < 8) { toast.error("Mot de passe : 8 caractères minimum"); return; }
        } else if (password && password.length < 8) {
            toast.error("Mot de passe : 8 caractères minimum");
            return;
        }
        setSubmitting(true);
        // Build primary + additional from the set.
        let primary, additional;
        if (isAdmin) {
            primary = "admin";
            additional = [];
        } else {
            // Combinable roles = pre_recruteur, referent, coordinateur.
            // Pick first as primary, others as additional.
            const arr = Array.from(roleSet);
            primary = arr[0];
            additional = arr.slice(1);
        }
        const payload = {
            name: name.trim(),
            role: primary,
            additional_roles: additional,
            avatar_url: avatarUrl || null,
            phone: (phone || "").trim() || null,
            assigned_offer_ids: isAssignable ? Array.from(assigned) : [],
        };
        // iter110l · Coordinateur RH → régions + permissions granulaires
        // iter110n · CEO 2026-09-22 · BUGFIX — les 2 permissions
        // `can_manage_contracts` et `can_remove_from_pipeline` avaient
        // été ajoutées à la modale mais oubliées dans le payload → les
        // cases se cochaient visuellement, `PATCH /users/{id}` répondait
        // 200 (le backend accepte les extras), mais rien n'était persisté.
        // Résultat : Jimmy De Araujo (coord Occitanie) recevait toujours
        // « Permission "Gérer les contrats" requise ». Fix : on écrit
        // TOUS les flags de `perms` sans oublier.
        if (isCoordinateur) {
            payload.assigned_regions = Array.from(regions);
            payload.can_download_badges = !!perms.can_download_badges;
            payload.can_send_onboarding = !!perms.can_send_onboarding;
            payload.can_review_onboarding = !!perms.can_review_onboarding;
            payload.can_view_contracts = !!perms.can_view_contracts;
            payload.can_manage_contracts = !!perms.can_manage_contracts;
            payload.can_remove_from_pipeline = !!perms.can_remove_from_pipeline;
        }
        // CEO 2026-09-30 · Persiste les onglets masqués (on ne stocke
        // que ceux qui sont *éligibles* mais décochés — inutile de
        // conserver le path d'un onglet auquel le rôle ne donne plus
        // droit après un changement de rôle).
        const eligibleSet = new Set(eligibleTabs.map((t) => t.to));
        payload.hidden_nav = Array.from(hiddenNav).filter((p) => eligibleSet.has(p));
        if (isCreate) {
            payload.email = email.trim().toLowerCase();
            payload.password = password;
        } else if (password) {
            payload.password = password;
        }
        try {
            await onSubmit(payload);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 bg-shariti-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            data-testid={isCreate ? "user-create-modal" : "user-edit-modal"}
            onClick={onCancel}
        >
            <motion.form
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 max-w-2xl w-full max-h-[92vh] flex flex-col my-8"
            >
                <div className="flex items-start justify-between gap-4 mb-5 shrink-0">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                            <UsersIcon className="h-3 w-3" /> {isCreate ? "Nouvel utilisateur" : "Modifier l'utilisateur"}
                        </div>
                        <h2 className="mt-3 text-xl font-black tracking-tight text-shariti-black">
                            {isCreate ? "Créer un compte" : user.name}
                        </h2>
                        <p className="mt-1 text-sm font-medium text-slate-500">
                            {isCreate ? "L'utilisateur pourra se connecter avec l'email + mot de passe que vous définissez." : "Modifiez les infos. Laissez le mot de passe vide pour le conserver."}
                        </p>
                    </div>
                    <button type="button" onClick={onCancel} className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-500 hover:text-shariti-red hover:bg-red-50 transition shrink-0">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 -mx-2 px-2 space-y-4">
                    {/* Photo de profil */}
                    <Field label="Photo de profil">
                        <AvatarUploader
                            value={avatarUrl}
                            name={name || user?.name}
                            onChange={setAvatarUrl}
                            size={88}
                            testId="user-form-avatar"
                        />
                    </Field>

                    <Field label="Nom complet" testId="user-form-name">
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="ex. Sarah Ben Ali" className="input-shariti" />
                    </Field>

                    <Field label="Email (identifiant de connexion)" testId="user-form-email">
                        <input
                            type="email"
                            value={email}
                            disabled={!isCreate}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="ex. sarah@shariti.com"
                            className="input-shariti disabled:bg-shariti-cream disabled:text-slate-400 disabled:cursor-not-allowed"
                        />
                        {!isCreate && <p className="mt-1 text-[10px] font-medium text-slate-400">L'email d'un compte existant ne peut pas être modifié.</p>}
                    </Field>

                    <Field label={isCreate ? "Mot de passe initial" : "Nouveau mot de passe (laisser vide pour conserver)"} testId="user-form-password">
                        <input
                            type="text"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="8 caractères minimum"
                            autoComplete="new-password"
                            className="input-shariti"
                        />
                    </Field>

                    <Field label="Téléphone (visible par les candidats si Référent)" testId="user-form-phone">
                        <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="ex. 06 12 34 56 78"
                            autoComplete="tel"
                            className="input-shariti"
                        />
                        <p className="mt-1 text-[10px] font-medium text-slate-400">
                            Ce numéro est injecté dans le SMS récap envoyé au candidat (variable <span className="font-mono text-shariti-orange">{"{{referent_phone}}"}</span>) — pour qu'il puisse vous joindre en cas de souci sur place.
                        </p>
                    </Field>

                    {/* Multi-role selector */}
                    <Field label="Rôles">
                        <p className="text-[10px] font-medium text-slate-400 mb-2">
                            Pré-recruteur et Référent terrain peuvent être combinés sur le même compte. Admin reste un rôle exclusif.
                        </p>
                        <div className="space-y-2">
                            {/* Pre-recruteur + Referent: checkbox style */}
                            {COMBINABLE_ROLES.map((rc) => {
                                const active = roleSet.has(rc.key);
                                return (
                                    <button
                                        key={rc.key}
                                        type="button"
                                        onClick={() => toggleRole(rc.key)}
                                        data-testid={`user-form-role-${rc.key}`}
                                        className={`w-full flex items-start gap-3 p-3 rounded-2xl border-2 transition text-left ${
                                            active ? "bg-shariti-orange-soft border-shariti-orange/40" : "bg-white border-shariti-gray hover:border-shariti-orange/40"
                                        }`}
                                    >
                                        <div className={`h-5 w-5 rounded-md border-2 flex items-center justify-center shrink-0 transition mt-0.5 ${active ? "bg-shariti-orange border-shariti-orange" : "bg-white border-shariti-gray"}`}>
                                            {active && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className={`text-sm font-extrabold ${active ? "text-shariti-black" : "text-slate-600"}`}>{rc.label}</div>
                                            <div className="text-[11px] font-medium text-slate-500 mt-0.5">{rc.desc}</div>
                                        </div>
                                    </button>
                                );
                            })}
                            {/* Admin: separated radio-style (exclusive) */}
                            <div className="pt-1">
                                <button
                                    type="button"
                                    onClick={() => toggleRole("admin")}
                                    data-testid="user-form-role-admin"
                                    className={`w-full flex items-start gap-3 p-3 rounded-2xl border-2 transition text-left ${
                                        isAdmin ? "bg-shariti-black/95 border-shariti-black text-white" : "bg-white border-shariti-gray hover:border-shariti-black/40"
                                    }`}
                                >
                                    <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 transition mt-0.5 ${isAdmin ? "bg-white border-white" : "bg-white border-shariti-gray"}`}>
                                        {isAdmin && <div className="h-2.5 w-2.5 rounded-full bg-shariti-black" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className={`text-sm font-extrabold ${isAdmin ? "text-white" : "text-slate-600"}`}>Back Office RH (Admin)</div>
                                        <div className={`text-[11px] font-medium mt-0.5 ${isAdmin ? "text-white/80" : "text-slate-500"}`}>Accès complet · gestion de l'équipe · trames d'entretien</div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </Field>

                    {isAssignable && (
                        <Field label="Périmètre offres (cochez pour restreindre)">
                            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtrer (titre, ville, #ID)..." className="input-shariti mb-2" />
                            <div className="max-h-44 overflow-y-auto space-y-1 rounded-2xl bg-shariti-cream/40 p-2">
                                {filteredOffers.length === 0 ? (
                                    <div className="text-center py-3 text-xs text-slate-400 font-medium">Aucune offre ne correspond.</div>
                                ) : filteredOffers.map((o) => {
                                    const active = assigned.has(o.layan_offer_id);
                                    return (
                                        <button
                                            key={o.layan_offer_id}
                                            type="button"
                                            onClick={() => toggleOffer(o.layan_offer_id)}
                                            className={`w-full text-left flex items-center gap-2 p-2 rounded-xl border transition ${
                                                active ? "bg-shariti-orange-soft border-shariti-orange/40" : "bg-white border-transparent hover:border-shariti-orange/30"
                                            }`}
                                        >
                                            <div className={`h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 ${active ? "bg-shariti-orange border-shariti-orange" : "bg-white border-shariti-gray"}`}>
                                                {active && <CheckCircle2 className="h-3 w-3 text-white" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[11px] font-bold text-shariti-black truncate">{o.title}</div>
                                                <div className="text-[10px] font-medium text-slate-400">#{o.layan_offer_id} · {o.city || "—"} · {o.status}</div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="mt-1 text-[10px] font-medium text-slate-400">
                                {assigned.size === 0
                                    ? <span className="text-emerald-600 font-bold">→ Voit toutes les offres</span>
                                    : <span className="text-shariti-orange font-bold">→ Restreint à {assigned.size} offre{assigned.size > 1 ? "s" : ""}</span>}
                            </p>
                        </Field>
                    )}

                    {/* iter110l · Coordinateur RH — régions + permissions granulaires */}
                    {isCoordinateur && (
                        <>
                            <Field label="Zones régionales autorisées">                                <p className="text-[10px] font-medium text-slate-400 mb-2">
                                    Ce Coordinateur ne verra QUE les candidats dont l'offre est dans ces zones. Sans zone cochée, il ne verra aucun candidat.
                                </p>
                                <div className="grid grid-cols-2 gap-2" data-testid="user-form-regions">
                                    {COORDINATEUR_REGIONS.map((r) => {
                                        const active = regions.has(r);
                                        return (
                                            <button
                                                key={r}
                                                type="button"
                                                onClick={() => toggleRegion(r)}
                                                data-testid={`user-form-region-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                className={`flex items-center gap-2 p-2.5 rounded-2xl border-2 transition text-left ${
                                                    active ? "bg-indigo-50 border-indigo-400" : "bg-white border-shariti-gray hover:border-indigo-300"
                                                }`}
                                            >
                                                <div className={`h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 ${active ? "bg-indigo-600 border-indigo-600" : "bg-white border-shariti-gray"}`}>
                                                    {active && <CheckCircle2 className="h-3 w-3 text-white" />}
                                                </div>
                                                <span className="text-[12px] font-bold text-shariti-black">{r}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="mt-2 text-[10px] font-medium">
                                    {regions.size === 0
                                        ? <span className="text-red-600 font-bold">⚠ Aucune zone cochée — ce coordinateur ne verra AUCUN candidat</span>
                                        : <span className="text-indigo-600 font-bold">→ Visibilité limitée à {regions.size} zone{regions.size > 1 ? "s" : ""}</span>}
                                </p>
                            </Field>

                            <Field label="Permissions (cochez ce que ce Coordinateur peut faire)">
                                <p className="text-[10px] font-medium text-slate-400 mb-2">
                                    Par défaut aucune permission n'est cochée — lecture seule. Cochez les actions qu'il peut effectuer dans son périmètre.
                                </p>
                                <div className="rounded-2xl bg-indigo-50/60 border border-indigo-200 p-3 mb-3">
                                    <p className="text-[11px] font-bold text-indigo-900 uppercase tracking-wider mb-1">
                                        Visibilité des emails
                                    </p>
                                    <p className="text-[11px] font-medium text-indigo-900/80 leading-relaxed">
                                        Chaque email déclenché par le Coordinateur (envoi fiche, validation, contrat…) le met automatiquement <span className="font-bold">en copie</span> — il voit ce qu'il envoie sans passer par un chargé RH. Il est aussi <span className="font-bold">notifié</span> quand un candidat de sa région resoumet un dossier.
                                    </p>
                                </div>
                                <div className="space-y-2" data-testid="user-form-permissions">
                                    {COORDINATEUR_PERMISSIONS.map((p) => {
                                        const active = !!perms[p.key];
                                        return (
                                            <button
                                                key={p.key}
                                                type="button"
                                                onClick={() => togglePerm(p.key)}
                                                data-testid={`user-form-perm-${p.key}`}
                                                className={`w-full flex items-start gap-3 p-3 rounded-2xl border-2 transition text-left ${
                                                    active ? "bg-indigo-50 border-indigo-400" : "bg-white border-shariti-gray hover:border-indigo-300"
                                                }`}
                                            >
                                                <div className={`h-5 w-5 rounded-md border-2 flex items-center justify-center shrink-0 transition mt-0.5 ${active ? "bg-indigo-600 border-indigo-600" : "bg-white border-shariti-gray"}`}>
                                                    {active && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className={`text-sm font-extrabold ${active ? "text-shariti-black" : "text-slate-600"}`}>{p.label}</div>
                                                    <div className="text-[11px] font-medium text-slate-500 mt-0.5">{p.desc}</div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </Field>
                        </>
                    )}

                    {/* CEO 2026-09-30 · Onglets sidebar visibles pour ce
                        user. Case cochée = onglet visible. Toujours affiché
                        (peu importe le rôle), quand aucun onglet n'est
                        éligible on affiche un message doux plutôt qu'un vide.
                    */}
                    <Field label="Onglets accessibles dans la sidebar">
                        <p className="text-[10px] font-medium text-slate-400 mb-2">
                            Cochez les onglets que ce compte peut voir. Par défaut, tous les onglets autorisés par le rôle sont visibles. Décochez pour masquer un onglet à ce user précis (utile pour un référent qui n'a pas à gérer les permanents, un pré-recruteur qui n'a pas à voir le vivier, etc.).
                        </p>
                        {eligibleTabs.length === 0 ? (
                            <div className="text-center py-4 text-xs text-slate-400 italic bg-shariti-cream/30 rounded-2xl">
                                Aucun onglet accessible avec les rôles actuels — choisissez au moins un rôle plus haut.
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center gap-2 mb-2">
                                    <button
                                        type="button"
                                        data-testid="user-form-nav-select-all"
                                        onClick={() => setHiddenNav(new Set())}
                                        className="text-[10px] font-bold uppercase tracking-widest text-shariti-orange hover:underline"
                                    >
                                        Tout cocher
                                    </button>
                                    <span className="text-[10px] text-slate-300">·</span>
                                    <button
                                        type="button"
                                        data-testid="user-form-nav-clear-all"
                                        onClick={() => setHiddenNav(new Set(eligibleTabs.map((t) => t.to)))}
                                        className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:underline"
                                    >
                                        Tout décocher
                                    </button>
                                    <span className="ml-auto text-[10px] font-bold text-slate-400">
                                        {eligibleTabs.length - hiddenNav.size} / {eligibleTabs.length} visibles
                                    </span>
                                </div>
                                <div
                                    className="grid grid-cols-1 md:grid-cols-2 gap-1.5 rounded-2xl bg-shariti-cream/40 p-2 max-h-64 overflow-y-auto"
                                    data-testid="user-form-nav-list"
                                >
                                    {eligibleTabs.map((t) => {
                                        const visible = !hiddenNav.has(t.to);
                                        const Icon = t.icon;
                                        return (
                                            <button
                                                key={t.to}
                                                type="button"
                                                onClick={() => toggleTab(t.to)}
                                                data-testid={`user-form-nav-${t.to.replace(/[^a-z0-9]+/gi, "-")}`}
                                                className={`flex items-center gap-2 p-2 rounded-xl border transition text-left ${
                                                    visible
                                                        ? "bg-shariti-orange-soft border-shariti-orange/40"
                                                        : "bg-white border-shariti-gray hover:border-shariti-orange/30"
                                                }`}
                                            >
                                                <div className={`h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 ${visible ? "bg-shariti-orange border-shariti-orange" : "bg-white border-shariti-gray"}`}>
                                                    {visible && <CheckCircle2 className="h-3 w-3 text-white" />}
                                                </div>
                                                {Icon && <Icon className={`h-3.5 w-3.5 shrink-0 ${visible ? "text-shariti-orange" : "text-slate-400"}`} />}
                                                <span className={`text-[11px] font-bold truncate ${visible ? "text-shariti-black" : "text-slate-400"}`}>
                                                    {t.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="mt-1.5 text-[10px] font-medium">
                                    {hiddenNav.size === 0
                                        ? <span className="text-emerald-600 font-bold">→ Tous les onglets autorisés seront visibles</span>
                                        : <span className="text-shariti-orange font-bold">→ {hiddenNav.size} onglet{hiddenNav.size > 1 ? "s" : ""} masqué{hiddenNav.size > 1 ? "s" : ""} sur ce compte</span>}
                                </p>
                            </>
                        )}
                    </Field>
                </div>

                <div className="mt-5 flex items-center justify-end gap-3 shrink-0 border-t border-shariti-gray pt-4">
                    <button type="button" onClick={onCancel} className="inline-flex items-center gap-2 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-red hover:border-shariti-red px-5 h-10 font-bold text-xs transition">
                        Annuler
                    </button>
                    <button type="submit" disabled={submitting} data-testid="user-form-submit" className="btn-shariti disabled:opacity-60">
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {isCreate ? "Créer l'utilisateur" : "Enregistrer"}
                    </button>
                </div>
            </motion.form>
        </motion.div>
    );
}

function Field({ label, children, testId }) {
    return (
        <label className="block" data-testid={testId}>
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">{label}</span>
            {children}
        </label>
    );
}

function ScopeEditor({ user, offers, onCancel, onSave }) {
    const [selected, setSelected] = useState(new Set(user.assigned_offer_ids || []));
    const [search, setSearch] = useState("");

    function toggle(oid) {
        const next = new Set(selected);
        if (next.has(oid)) next.delete(oid);
        else next.add(oid);
        setSelected(next);
    }

    const filtered = offers.filter(
        (o) => !search
            || o.title.toLowerCase().includes(search.toLowerCase())
            || o.city?.toLowerCase().includes(search.toLowerCase())
            || String(o.layan_offer_id).includes(search),
    );

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 bg-shariti-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            data-testid="scope-editor"
            onClick={onCancel}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 max-w-2xl w-full max-h-[85vh] flex flex-col my-8"
            >
                <div className="flex items-start justify-between gap-4 mb-5 shrink-0">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                            <Target className="h-3 w-3" /> Périmètre offres
                        </div>
                        <h2 className="mt-3 text-xl font-black tracking-tight text-shariti-black">{user.name}</h2>
                        <p className="mt-1 text-sm font-medium text-slate-500">
                            Cochez les offres Layan que cet utilisateur doit voir.
                            <strong className="text-shariti-orange"> Aucune coche</strong> = voit toutes les offres (comportement par défaut).
                        </p>
                    </div>
                    <button onClick={onCancel} className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-500 hover:text-shariti-red hover:bg-red-50 transition shrink-0">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Filtrer les offres (titre, ville, #ID)..."
                    data-testid="scope-editor-search"
                    className="input-shariti mb-3 shrink-0"
                />

                <div className="overflow-y-auto flex-1 -mx-2 px-2 space-y-1.5">
                    {filtered.length === 0 ? (
                        <div className="text-center py-8 text-sm text-slate-400 font-medium">Aucune offre ne correspond à votre recherche.</div>
                    ) : (
                        filtered.map((o) => {
                            const active = selected.has(o.layan_offer_id);
                            return (
                                <button
                                    key={o.layan_offer_id}
                                    type="button"
                                    onClick={() => toggle(o.layan_offer_id)}
                                    data-testid={`scope-offer-${o.layan_offer_id}`}
                                    className={`w-full text-left flex items-start gap-3 p-3 rounded-2xl border-2 transition ${
                                        active ? "bg-shariti-orange-soft border-shariti-orange/40" : "bg-white border-shariti-gray hover:border-shariti-orange/40"
                                    }`}
                                >
                                    <div className={`h-5 w-5 rounded-md border-2 flex items-center justify-center shrink-0 transition ${active ? "bg-shariti-orange border-shariti-orange" : "bg-white border-shariti-gray"}`}>
                                        {active && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">#{o.layan_offer_id}</span>
                                            <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-shariti-cream text-slate-500 border border-shariti-gray">{o.status}</span>
                                            {o.city && <span className="text-[10px] font-bold text-slate-500">{o.city}</span>}
                                        </div>
                                        <div className="text-sm font-extrabold text-shariti-black mt-0.5 line-clamp-1">{o.title}</div>
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>

                <div className="mt-5 flex items-center justify-between gap-3 shrink-0 border-t border-shariti-gray pt-4">
                    <div className="text-xs font-bold text-shariti-black">
                        {selected.size === 0 ? (
                            <span className="text-emerald-600">→ Voit toutes les offres</span>
                        ) : (
                            <span className="text-shariti-orange">→ Restreint à {selected.size} offre{selected.size > 1 ? "s" : ""}</span>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={onCancel} className="inline-flex items-center gap-2 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-red hover:border-shariti-red px-5 h-10 font-bold text-xs transition">Annuler</button>
                        <button onClick={() => onSave(Array.from(selected))} data-testid="scope-editor-save" className="btn-shariti">
                            <Save className="h-4 w-4" />
                            Enregistrer
                        </button>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}
